/**
 * Audio feedback system for KeyboardHero
 * Handles playing correct/incorrect sounds
 */

export interface AudioFeedbackConfig {
  enableSounds: boolean;
  correctSound: string;
  incorrectSound: string;
  volume: number;
}

class AudioFeedbackManager {
  private config: AudioFeedbackConfig;
  private audioContext: AudioContext | null = null;
  private correctSound: AudioBuffer | null = null;
  private incorrectSound: AudioBuffer | null = null;

  constructor(config: AudioFeedbackConfig) {
    this.config = config;
    this.initializeAudioContext();
  }

  private async initializeAudioContext() {
    try {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      await this.loadSounds();
    } catch (error) {
      console.warn('Audio context initialization failed:', error);
    }
  }

  private async loadSounds() {
    if (!this.audioContext) return;

    try {
      // Load the actual WAV files
      const [correctBuffer, incorrectBuffer] = await Promise.all([
        this.loadAudioBuffer('/assets/pickupCoin.wav'),
        this.loadAudioBuffer('/assets/fail.wav')
      ]);
      
      this.correctSound = correctBuffer;
      this.incorrectSound = incorrectBuffer;
    } catch (error) {
      console.warn('Failed to load audio files, falling back to synthetic sounds:', error);
      // Fallback to synthetic sounds
      this.correctSound = this.createWaterDropSound();
      this.incorrectSound = this.createPigGruntSound();
    }
  }

  private async loadAudioBuffer(url: string): Promise<AudioBuffer> {
    if (!this.audioContext) throw new Error('No audio context');
    
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch audio file: ${url}`);
    }
    
    const arrayBuffer = await response.arrayBuffer();
    return await this.audioContext.decodeAudioData(arrayBuffer);
  }

  private createWaterDropSound(): AudioBuffer {
    if (!this.audioContext) throw new Error('No audio context');
    
    const sampleRate = this.audioContext.sampleRate;
    const duration = 0.3;
    const bufferSize = sampleRate * duration;
    const buffer = this.audioContext.createBuffer(1, bufferSize, sampleRate);
    const data = buffer.getChannelData(0);

    for (let i = 0; i < bufferSize; i++) {
      const t = i / sampleRate;
      const frequency = 800 * Math.exp(-t * 8);
      const amplitude = Math.exp(-t * 6) * 0.3;
      data[i] = Math.sin(2 * Math.PI * frequency * t) * amplitude;
    }

    return buffer;
  }

  private createPigGruntSound(): AudioBuffer {
    if (!this.audioContext) throw new Error('No audio context');
    
    const sampleRate = this.audioContext.sampleRate;
    const duration = 0.2;
    const bufferSize = sampleRate * duration;
    const buffer = this.audioContext.createBuffer(1, bufferSize, sampleRate);
    const data = buffer.getChannelData(0);

    for (let i = 0; i < bufferSize; i++) {
      const t = i / sampleRate;
      const noise = (Math.random() - 0.5) * 2;
      const frequency = 150 + 50 * Math.sin(t * 30);
      const amplitude = Math.exp(-t * 8) * 0.4;
      data[i] = (noise * 0.3 + Math.sin(2 * Math.PI * frequency * t) * 0.7) * amplitude;
    }

    return buffer;
  }

  public playCorrectSound() {
    if (!this.config.enableSounds || !this.audioContext || !this.correctSound) return;

    try {
      const source = this.audioContext.createBufferSource();
      const gainNode = this.audioContext.createGain();
      
      source.buffer = this.correctSound;
      gainNode.gain.value = this.config.volume;
      
      source.connect(gainNode);
      gainNode.connect(this.audioContext.destination);
      
      source.start(0);
    } catch (error) {
      console.warn('Failed to play correct sound:', error);
    }
  }

  public playIncorrectSound() {
    if (!this.config.enableSounds || !this.audioContext || !this.incorrectSound) return;

    try {
      const source = this.audioContext.createBufferSource();
      const gainNode = this.audioContext.createGain();
      
      source.buffer = this.incorrectSound;
      gainNode.gain.value = this.config.volume;
      
      source.connect(gainNode);
      gainNode.connect(this.audioContext.destination);
      
      source.start(0);
    } catch (error) {
      console.warn('Failed to play incorrect sound:', error);
    }
  }

  public updateConfig(newConfig: AudioFeedbackConfig) {
    this.config = { ...this.config, ...newConfig };
  }

  public setVolume(volume: number) {
    this.config.volume = Math.max(0, Math.min(1, volume));
  }

  public toggleSounds(enabled: boolean) {
    this.config.enableSounds = enabled;
  }
}

const DEFAULT_AUDIO_CONFIG: AudioFeedbackConfig = {
  enableSounds: true,
  correctSound: '/assets/pickupCoin.wav',
  incorrectSound: '/assets/fail.wav',
  volume: 0.5
};

let audioManager: AudioFeedbackManager | null = null;

export const getAudioFeedbackManager = (): AudioFeedbackManager => {
  if (!audioManager) {
    audioManager = new AudioFeedbackManager(DEFAULT_AUDIO_CONFIG);
  }
  return audioManager;
};

export { AudioFeedbackManager };